import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Repeat, Plus, Trash2, ArrowLeft, Save, Search, X, Type, CircleDashed, GripVertical, Wrench, Eye, Mail } from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import KatalogAutocomplete from '../components/Katalog/KatalogAutocomplete';
import DokumentVorschau from '../components/Dokumente/DokumentVorschau';
import { wiederkehrendeRechnungApi } from '../api/wiederkehrendeRechnungApi';
import { getAllKunden } from '../api/kundenApi';

function DauerauftragErstellen() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditMode = !!editId;

  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Formular
  const [name, setName] = useState('');
  const [kundeId, setKundeId] = useState('');
  const [intervall, setIntervall] = useState('MONATLICH');
  const [naechsteFaelligkeit, setNaechsteFaelligkeit] = useState('');
  const [betreff, setBetreff] = useState('');
  const [kundenReferenz, setKundenReferenz] = useState('');
  const [einleitung, setEinleitung] = useState('Vielen Dank für Ihren Auftrag. Wir berechnen Ihnen wie folgt:');
  const [schlusstext, setSchlusstext] = useState('Bitte überweisen Sie den Betrag innerhalb der Zahlungsfrist auf unser Konto. Vielen Dank!');
  const [zahlungsziel, setZahlungsziel] = useState(14);
  const [zahlungsbedingungen, setZahlungsbedingungen] = useState('');
  const [leistungsdatum, setLeistungsdatum] = useState('');
  const [gesamtRabattProzent, setGesamtRabattProzent] = useState(0);
  const [skontoProzent, setSkontoProzent] = useState('');
  const [skontoTage, setSkontoTage] = useState('');
  const [zuschlagBetrag, setZuschlagBetrag] = useState('');
  const [zuschlagBeschreibung, setZuschlagBeschreibung] = useState('');
  const [direktVersenden, setDirektVersenden] = useState(false);

  const [positionen, setPositionen] = useState([
    { beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: '', ustSatz: 19, istLohn: false, positionsTyp: 'NORMAL', rabattProzent: 0 }
  ]);

  // Kundensuche
  const [kundenSuche, setKundenSuche] = useState('');
  const [showKundenDropdown, setShowKundenDropdown] = useState(false);
  const [selectedKunde, setSelectedKunde] = useState(null);
  const kundenDropdownRef = useRef(null);

  // Vorschau
  const [showVorschauModal, setShowVorschauModal] = useState(false);

  // Erstellte Rechnungen (nur im Edit-Modus)
  const [erstellteRechnungen, setErstellteRechnungen] = useState([]);

  useEffect(() => {
    loadKunden();
    if (isEditMode) loadExisting();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (kundenDropdownRef.current && !kundenDropdownRef.current.contains(e.target)) {
        setShowKundenDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadKunden = async () => {
    try {
      const data = await getAllKunden();
      setKunden(data);
    } catch (error) {
      console.error('Fehler beim Laden der Kunden:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExisting = async () => {
    try {
      const data = await wiederkehrendeRechnungApi.getById(editId);
      setName(data.name);
      setKundeId(data.kundeId);
      setIntervall(data.intervall);
      setNaechsteFaelligkeit(new Date(data.naechsteFaelligkeit).toISOString().split('T')[0]);
      setBetreff(data.betreff || '');
      setKundenReferenz(data.kundenReferenz || '');
      setEinleitung(data.einleitung || '');
      setSchlusstext(data.schlusstext || '');
      setZahlungsziel(data.zahlungsziel || 14);
      setZahlungsbedingungen(data.zahlungsbedingungen || '');
      setLeistungsdatum(data.leistungsdatum || '');
      setGesamtRabattProzent(data.gesamtRabattProzent || 0);
      setSkontoProzent(data.skontoProzent || '');
      setSkontoTage(data.skontoTage || '');
      setZuschlagBetrag(data.zuschlagBetrag ? data.zuschlagBetrag / 100 : '');
      setZuschlagBeschreibung(data.zuschlagBeschreibung || '');
      setDirektVersenden(data.direktVersenden || false);
      setErstellteRechnungen(data.erstellteRechnungen || []);

      if (data.kunde) {
        setSelectedKunde(data.kunde);
        setKundenSuche(data.kunde.name);
      }

      // Positionen: Preise von Cent zu Euro
      if (data.positionen?.length > 0) {
        setPositionen(data.positionen.map(pos => ({
          ...pos,
          einzelpreis: pos.einzelpreis ? pos.einzelpreis / 100 : '',
          berechnungsSchritte: pos.berechnungsSchritte
            ? pos.berechnungsSchritte.map(s => ({ ...s, einzelpreis: s.einzelpreis / 100, gesamt: s.gesamt / 100 }))
            : undefined
        })));
      }
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      navigate('/rechnungen');
    }
  };

  const selectKunde = (kunde) => {
    setKundeId(kunde.id);
    setSelectedKunde(kunde);
    setKundenSuche(kunde.name);
    setShowKundenDropdown(false);
  };

  const clearKunde = () => {
    setKundeId('');
    setSelectedKunde(null);
    setKundenSuche('');
  };

  const filteredKunden = kunden.filter(k =>
    k.name.toLowerCase().includes(kundenSuche.toLowerCase()) ||
    (k.email && k.email.toLowerCase().includes(kundenSuche.toLowerCase()))
  );

  // Position-Helpers
  const addPosition = () => setPositionen([...positionen, { beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: '', ustSatz: 19, istLohn: false, positionsTyp: 'NORMAL', rabattProzent: 0 }]);
  const addTextPosition = () => setPositionen([...positionen, { beschreibung: '', menge: 0, einheit: '', einzelpreis: 0, ustSatz: 0, istLohn: false, positionsTyp: 'TEXT', rabattProzent: 0 }]);
  const addOptionalPosition = () => setPositionen([...positionen, { beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: '', ustSatz: 19, istLohn: false, positionsTyp: 'OPTIONAL', rabattProzent: 0 }]);

  const removePosition = (index) => {
    if (positionen.length > 1) setPositionen(positionen.filter((_, i) => i !== index));
  };

  const updatePosition = (index, field, value) => {
    const updated = [...positionen];
    updated[index] = { ...updated[index], [field]: value };
    setPositionen(updated);
  };

  const movePosition = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= positionen.length) return;
    const updated = [...positionen];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setPositionen(updated);
  };

  // Berechnungen
  const calculatePositionGesamt = (pos) => {
    if (pos.positionsTyp === 'TEXT') return 0;
    const gesamt = (parseFloat(pos.einzelpreis) || 0) * (parseFloat(pos.menge) || 0);
    const rabatt = parseFloat(pos.rabattProzent) || 0;
    return rabatt > 0 ? gesamt * (1 - rabatt / 100) : gesamt;
  };

  const calculateNetto = () => {
    let netto = positionen
      .filter(p => p.positionsTyp === 'NORMAL')
      .reduce((sum, p) => sum + calculatePositionGesamt(p), 0);
    const rabatt = parseFloat(gesamtRabattProzent) || 0;
    if (rabatt > 0) netto = netto * (1 - rabatt / 100);
    netto += parseFloat(zuschlagBetrag) || 0;
    return netto;
  };

  const calculateUst = () => {
    const rabatt = parseFloat(gesamtRabattProzent) || 0;
    let ust = positionen
      .filter(p => p.positionsTyp === 'NORMAL')
      .reduce((sum, p) => {
        let posGesamt = calculatePositionGesamt(p);
        if (rabatt > 0) posGesamt = posGesamt * (1 - rabatt / 100);
        return sum + posGesamt * ((parseFloat(p.ustSatz) || 0) / 100);
      }, 0);
    const zuschlag = parseFloat(zuschlagBetrag) || 0;
    if (zuschlag > 0) ust += zuschlag * 0.19;
    return ust;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!kundeId) return alert('Bitte Kunde auswählen');
    if (!name.trim()) return alert('Bitte Name eingeben');
    if (!naechsteFaelligkeit) return alert('Bitte nächste Fälligkeit eingeben');

    const validPositionen = positionen.filter(p => p.beschreibung.trim());
    if (validPositionen.length === 0) return alert('Mindestens eine Position erforderlich');

    setSaving(true);
    try {
      // Preise in Cent umrechnen
      const positionenInCent = validPositionen.map(pos => ({
        ...pos,
        einzelpreis: Math.round((parseFloat(pos.einzelpreis) || 0) * 100),
        berechnungsSchritte: pos.berechnungsSchritte
          ? pos.berechnungsSchritte.map(s => ({
              ...s,
              einzelpreis: Math.round((parseFloat(s.einzelpreis) || 0) * 100),
              gesamt: Math.round((parseFloat(s.gesamt) || 0) * 100)
            }))
          : undefined
      }));

      const payload = {
        name: name.trim(),
        kundeId,
        intervall,
        naechsteFaelligkeit,
        betreff: betreff || undefined,
        kundenReferenz: kundenReferenz || undefined,
        einleitung: einleitung || undefined,
        schlusstext: schlusstext || undefined,
        zahlungsziel: parseInt(zahlungsziel) || 14,
        zahlungsbedingungen: zahlungsbedingungen || undefined,
        leistungsdatum: leistungsdatum || undefined,
        gesamtRabattProzent: parseFloat(gesamtRabattProzent) || 0,
        skontoProzent: skontoProzent ? parseFloat(skontoProzent) : undefined,
        skontoTage: skontoTage ? parseInt(skontoTage) : undefined,
        zuschlagBetrag: zuschlagBetrag ? Math.round(parseFloat(zuschlagBetrag) * 100) : 0,
        zuschlagBeschreibung: zuschlagBeschreibung || undefined,
        positionen: positionenInCent,
        direktVersenden
      };

      if (isEditMode) {
        await wiederkehrendeRechnungApi.update(editId, payload);
      } else {
        await wiederkehrendeRechnungApi.create(payload);
      }

      navigate('/rechnungen');
    } catch (error) {
      alert('Fehler: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Layout><div className="p-8 text-center">Laden...</div></Layout>;

  // Vorschau-Daten für DokumentVorschau (gleiche Struktur wie RechnungErstellen)
  const vorschauData = {
    type: 'rechnung',
    kunde: selectedKunde,
    betreff,
    kundenReferenz,
    einleitung,
    schlusstext,
    zahlungsbedingungen,
    positionen,
    gesamtRabattProzent,
    zuschlagBetrag,
    zuschlagBeschreibung,
    skontoProzent,
    skontoTage,
    leistungsdatum,
    zahlungsziel,
  };

  return (
    <Layout>
      <div className="max-w-[1800px] mx-auto p-4 sm:p-8">
        <button onClick={() => navigate('/rechnungen')} className="flex items-center gap-2 text-text-muted hover:text-primary mb-6">
          <ArrowLeft className="w-4 h-4" />
          Zurück zu Rechnungen
        </button>

        <div className="flex items-center gap-3 mb-6">
          <Repeat className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold">{isEditMode ? 'Dauerauftrag bearbeiten' : 'Neuer Dauerauftrag'}</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex gap-6">
          <div className="flex-1 min-w-0 space-y-6">
          {/* Grunddaten */}
          <div className="bg-surface rounded-card shadow-card p-6">
            <h2 className="text-lg font-semibold mb-4">Grunddaten</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium mb-1">Name des Dauerauftrags *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="z.B. Monatliche Wartung Müller"
                  className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary"
                  required
                />
              </div>

              {/* Kundensuche */}
              <div className="sm:col-span-2" ref={kundenDropdownRef}>
                <label className="block text-sm font-medium mb-1">Kunde *</label>
                {selectedKunde ? (
                  <div className="flex items-center gap-2 px-3 py-2 border border-border rounded-button bg-gray-50">
                    <span className="flex-1 font-medium">{selectedKunde.name}</span>
                    <span className="text-sm text-text-muted">{selectedKunde.email}</span>
                    <button type="button" onClick={clearKunde} className="text-text-muted hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-text-muted" />
                    <input
                      type="text"
                      value={kundenSuche}
                      onChange={(e) => { setKundenSuche(e.target.value); setShowKundenDropdown(true); }}
                      onFocus={() => setShowKundenDropdown(true)}
                      placeholder="Kunde suchen..."
                      className="w-full pl-9 pr-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                    {showKundenDropdown && filteredKunden.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-surface border border-border rounded-button shadow-lg max-h-60 overflow-y-auto">
                        {filteredKunden.slice(0, 20).map(k => (
                          <button key={k.id} type="button" onClick={() => selectKunde(k)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex justify-between">
                            <span className="font-medium">{k.name}</span>
                            <span className="text-sm text-text-muted">{k.email}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Intervall *</label>
                <select value={intervall} onChange={(e) => setIntervall(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary">
                  <option value="MONATLICH">Monatlich</option>
                  <option value="QUARTALSWEISE">Quartalsweise</option>
                  <option value="HALBJAEHRLICH">Halbjährlich</option>
                  <option value="JAEHRLICH">Jährlich</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Nächste Fälligkeit *</label>
                <input
                  type="date"
                  value={naechsteFaelligkeit}
                  onChange={(e) => setNaechsteFaelligkeit(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Zahlungsziel (Tage)</label>
                <input
                  type="number"
                  value={zahlungsziel}
                  onChange={(e) => setZahlungsziel(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Leistungsdatum</label>
                <input
                  type="text"
                  value={leistungsdatum}
                  onChange={(e) => setLeistungsdatum(e.target.value)}
                  placeholder="z.B. 01.04.2026 oder leer = automatisch"
                  className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
            </div>
          </div>

          {/* Rechnungstexte */}
          <div className="bg-surface rounded-card shadow-card p-6">
            <h2 className="text-lg font-semibold mb-4">Rechnungstexte</h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Betreff</label>
                <input type="text" value={betreff} onChange={(e) => setBetreff(e.target.value)}
                  placeholder="Betreff der Rechnung"
                  className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Kundenreferenz</label>
                <input type="text" value={kundenReferenz} onChange={(e) => setKundenReferenz(e.target.value)}
                  placeholder="Ihre Zeichen / Auftragsnummer"
                  className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Einleitung</label>
                <textarea value={einleitung} onChange={(e) => setEinleitung(e.target.value)} rows={2}
                  className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Schlusstext</label>
                <textarea value={schlusstext} onChange={(e) => setSchlusstext(e.target.value)} rows={2}
                  className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Zahlungsbedingungen</label>
                <textarea value={zahlungsbedingungen} onChange={(e) => setZahlungsbedingungen(e.target.value)} rows={2}
                  placeholder="Optionale Zahlungsbedingungen..."
                  className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
            </div>
          </div>

          {/* Positionen */}
          <div className="bg-surface rounded-card shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Positionen</h2>
              <div className="flex items-center gap-2">
                <button type="button" onClick={addTextPosition} className="flex items-center gap-1 text-text-muted hover:text-primary text-sm">
                  <Type className="w-4 h-4" /> Textposition
                </button>
                <button type="button" onClick={addOptionalPosition} className="flex items-center gap-1 text-amber-600 hover:underline text-sm">
                  <CircleDashed className="w-4 h-4" /> Optionale Pos.
                </button>
                <button type="button" onClick={addPosition} className="flex items-center gap-1 text-primary hover:underline text-sm">
                  <Plus className="w-4 h-4" /> Position hinzufügen
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-text-muted mb-2">Schnellauswahl aus Katalog</label>
              <KatalogAutocomplete
                onSelect={(katalogPosition) => {
                  setPositionen([...positionen, {
                    beschreibung: katalogPosition.bezeichnung,
                    menge: 1,
                    einheit: katalogPosition.einheit,
                    einzelpreis: katalogPosition.verkaufspreis,
                    ustSatz: katalogPosition.mwstSatz,
                    istLohn: katalogPosition.istLohn || false,
                    positionsTyp: 'NORMAL',
                    rabattProzent: 0
                  }]);
                }}
              />
            </div>

            <div className="space-y-4">
              {positionen.map((pos, index) => (
                <div key={index} className={`border rounded-button p-4 ${
                  pos.positionsTyp === 'TEXT' ? 'border-dashed border-gray-300 bg-gray-50/50' :
                  pos.positionsTyp === 'OPTIONAL' ? 'border-dashed border-amber-300 bg-amber-50/30' : 'border-border'
                }`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col">
                        <button type="button" onClick={() => movePosition(index, -1)}
                          className="p-0.5 text-text-muted hover:text-primary disabled:opacity-30" disabled={index === 0}>
                          <GripVertical className="w-3 h-3 rotate-180" />
                        </button>
                        <button type="button" onClick={() => movePosition(index, 1)}
                          className="p-0.5 text-text-muted hover:text-primary disabled:opacity-30" disabled={index === positionen.length - 1}>
                          <GripVertical className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="text-sm font-medium text-text-muted">
                        {pos.positionsTyp === 'TEXT' ? 'Textposition' : pos.positionsTyp === 'OPTIONAL' ? 'Optional' : `Pos. ${index + 1}`}
                      </span>
                      {pos.positionsTyp === 'NORMAL' && (
                        <label className="flex items-center gap-1 text-xs text-text-muted cursor-pointer">
                          <input type="checkbox" checked={pos.istLohn} onChange={(e) => updatePosition(index, 'istLohn', e.target.checked)}
                            className="w-3 h-3" />
                          <Wrench className="w-3 h-3" /> Lohn
                        </label>
                      )}
                    </div>
                    <button type="button" onClick={() => removePosition(index)}
                      className="text-text-muted hover:text-red-500" disabled={positionen.length <= 1}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {pos.positionsTyp === 'TEXT' ? (
                    <textarea value={pos.beschreibung} onChange={(e) => updatePosition(index, 'beschreibung', e.target.value)}
                      placeholder="Textposition (z.B. Zwischenüberschrift)..."
                      rows={2} className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
                  ) : (
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-12">
                        <input type="text" value={pos.beschreibung} onChange={(e) => updatePosition(index, 'beschreibung', e.target.value)}
                          placeholder="Beschreibung" className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
                      </div>
                      <div className="col-span-6 sm:col-span-3">
                        <label className="block text-xs text-text-muted mb-1">Menge</label>
                        <input type="number" step="0.01" value={pos.menge} onChange={(e) => updatePosition(index, 'menge', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
                      </div>
                      <div className="col-span-6 sm:col-span-3">
                        <label className="block text-xs text-text-muted mb-1">Einheit</label>
                        <select value={pos.einheit} onChange={(e) => updatePosition(index, 'einheit', e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary">
                          <option value="Stk">Stk.</option>
                          <option value="Std">Std.</option>
                          <option value="m²">m²</option>
                          <option value="m">m</option>
                          <option value="lfm">lfm</option>
                          <option value="kg">kg</option>
                          <option value="Pauschale">Pauschale</option>
                        </select>
                      </div>
                      <div className="col-span-6 sm:col-span-3">
                        <label className="block text-xs text-text-muted mb-1">Einzelpreis (EUR)</label>
                        <input type="number" step="0.01" value={pos.einzelpreis} onChange={(e) => updatePosition(index, 'einzelpreis', e.target.value)}
                          placeholder="0,00" className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <label className="block text-xs text-text-muted mb-1">MwSt %</label>
                        <select value={pos.ustSatz} onChange={(e) => updatePosition(index, 'ustSatz', parseFloat(e.target.value))}
                          className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary">
                          <option value={19}>19%</option>
                          <option value={7}>7%</option>
                          <option value={0}>0%</option>
                        </select>
                      </div>
                      <div className="col-span-4 sm:col-span-1">
                        <label className="block text-xs text-text-muted mb-1">Rabatt</label>
                        <input type="number" step="0.1" value={pos.rabattProzent} onChange={(e) => updatePosition(index, 'rabattProzent', e.target.value)}
                          placeholder="0" className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary text-sm" />
                      </div>
                      <div className="col-span-12 text-right text-sm font-medium text-text-muted">
                        = {formatCurrency(calculatePositionGesamt(pos))} netto
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Rabatt & Zuschlag */}
          <div className="bg-surface rounded-card shadow-card p-6">
            <h2 className="text-lg font-semibold mb-4">Rabatt & Zuschlag</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Gesamtrabatt %</label>
                <input type="number" step="0.1" value={gesamtRabattProzent} onChange={(e) => setGesamtRabattProzent(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Zuschlag (EUR)</label>
                <input type="number" step="0.01" value={zuschlagBetrag} onChange={(e) => setZuschlagBetrag(e.target.value)}
                  placeholder="0,00" className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Zuschlag Beschreibung</label>
                <input type="text" value={zuschlagBeschreibung} onChange={(e) => setZuschlagBeschreibung(e.target.value)}
                  placeholder="z.B. Anfahrt" className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Skonto %</label>
                <input type="number" step="0.1" value={skontoProzent} onChange={(e) => setSkontoProzent(e.target.value)}
                  placeholder="z.B. 2" className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Skonto Tage</label>
                <input type="number" value={skontoTage} onChange={(e) => setSkontoTage(e.target.value)}
                  placeholder="z.B. 10" className="w-full px-3 py-2 border border-border rounded-button focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
            </div>
          </div>

          {/* Summen-Vorschau */}
          <div className="bg-surface rounded-card shadow-card p-6">
            <h2 className="text-lg font-semibold mb-4">Vorschau</h2>
            <div className="space-y-2 text-right">
              <div className="flex justify-between"><span className="text-text-muted">Netto:</span><span>{formatCurrency(calculateNetto())}</span></div>
              <div className="flex justify-between"><span className="text-text-muted">MwSt:</span><span>{formatCurrency(calculateUst())}</span></div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Brutto:</span><span>{formatCurrency(calculateNetto() + calculateUst())}</span>
              </div>
            </div>
          </div>

          {/* Auto-Versand */}
          <div className="bg-surface rounded-card shadow-card p-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={direktVersenden} onChange={(e) => setDirektVersenden(e.target.checked)}
                className="w-4 h-4 text-primary rounded" />
              <div>
                <span className="font-medium">Automatisch per E-Mail versenden</span>
                <p className="text-sm text-text-muted">Erstellte Rechnungen direkt an den Kunden senden</p>
              </div>
            </label>
          </div>

          {/* Erstellte Rechnungen (Edit Mode) */}
          {isEditMode && erstellteRechnungen.length > 0 && (
            <div className="bg-surface rounded-card shadow-card p-6">
              <h2 className="text-lg font-semibold mb-4">Erstellte Rechnungen ({erstellteRechnungen.length})</h2>
              <div className="space-y-2">
                {erstellteRechnungen.map(r => (
                  <div key={r.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div>
                      <span className="font-medium">{r.rechnungsnummer}</span>
                      <span className="text-sm text-text-muted ml-2">
                        {new Date(r.datum).toLocaleDateString('de-DE')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm">{formatCurrency(r.bruttogesamt / 100)}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        r.status === 'bezahlt' ? 'bg-green-50 text-green-700' :
                        r.status === 'versendet' ? 'bg-blue-50 text-blue-700' :
                        r.status === 'ueberfaellig' ? 'bg-red-50 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{r.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

              {/* Spacer for sticky action bar */}
              <div className="h-24 sm:h-20"></div>
          </div>

          {/* Desktop Live-Vorschau (2xl+) */}
          <div className="hidden 2xl:block w-[550px] shrink-0">
            <DokumentVorschau
              data={vorschauData}
            />
          </div>
          </div>

          {/* Sticky Action Bar */}
          <div className="fixed inset-x-0 bottom-[80px] sm:bottom-0 z-30 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]">
            <div className="container mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-2 sm:gap-3">
              {/* Vorschau Button */}
              <button type="button" onClick={() => setShowVorschauModal(true)} className="2xl:hidden flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-100 transition shrink-0">
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Vorschau</span>
              </button>
              {/* Mail Toggle */}
              {kundeId && selectedKunde && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-button border transition shrink-0 ${!selectedKunde.email ? 'border-red-200 bg-red-50/50' : direktVersenden ? 'bg-blue-50 border-blue-200' : 'border-border'}`}>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={direktVersenden} onChange={(e) => setDirektVersenden(e.target.checked)} disabled={!selectedKunde.email} className="sr-only peer" />
                    <div className="w-9 h-5 bg-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                  </label>
                  <Mail className={`w-4 h-4 ${direktVersenden && selectedKunde.email ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className="text-sm font-medium hidden md:inline">{!selectedKunde.email ? 'Keine E-Mail' : direktVersenden ? 'Auto-Versand' : 'Kein Versand'}</span>
                </div>
              )}
              <div className="flex-1" />
              <button type="button" onClick={() => navigate('/rechnungen')} className="px-3 py-2 text-sm text-text-muted hover:bg-gray-100 rounded-button transition hidden sm:block">Abbrechen</button>
              <button type="submit" disabled={saving}
                className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 text-sm sm:text-base">
                {saving ? (
                  <span>Speichern...</span>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span className="hidden sm:inline">{isEditMode ? 'Aktualisieren' : 'Dauerauftrag erstellen'}</span>
                    <span className="sm:hidden">{isEditMode ? 'Speichern' : 'Erstellen'}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Vorschau Modal (Mobile/Tablet) */}
          {showVorschauModal && (
            <DokumentVorschau
              isModal
              onClose={() => setShowVorschauModal(false)}
              data={vorschauData}
            />
          )}
        </form>
      </div>
    </Layout>
  );
}

export default DauerauftragErstellen;
